import React, { useLayoutEffect } from 'react';

import { MOBILE_MAX_WIDTH_PX, TABLET_MAX_WIDTH_PX } from '../../constants';

type ScreenSize = 'mobile' | 'tablet' | 'desktop';

const MEDIA_QUERIES = {
  mobile: `(max-width: ${MOBILE_MAX_WIDTH_PX}px)`,
  tablet: `(min-width: ${MOBILE_MAX_WIDTH_PX + 1}px) and (max-width: ${TABLET_MAX_WIDTH_PX}px)`,
  desktop: `(min-width: ${TABLET_MAX_WIDTH_PX + 1}px)`,
};

/**
 *  This hook returns whether the viewport matches the given screen size, or `null` when that
 *  isn't yet known. It should primarily be used for code pivots where it's impractical to use
 *  CSS directly.
 *
 *  Returns `null` (never `undefined`) whenever the result is unresolved — initial render, server
 *  rendering, or when `window.matchMedia` is unavailable — so the "not yet known" state is a
 *  single sentinel. Nullish-coalesce to a default for your initial/SSR state:
 *
 *  e.g. `const isDesktop = useMediaQuery('desktop') ?? true;`
 */
export function useMediaQuery(size: ScreenSize): boolean | null {
  const [isSize, setIsSize] = React.useState<boolean | null>(null);

  useLayoutEffect(() => {
    const query = window?.matchMedia?.(MEDIA_QUERIES[size]);
    // Coalesce a missing `matchMedia` (query `undefined` ⇒ `matches` `undefined`) into the
    // `null` "unresolved" sentinel so consumers only ever see `boolean | null`.
    setIsSize(query?.matches ?? null);

    function handleChange(event: MediaQueryListEvent) {
      setIsSize(event.matches);
    }

    query?.addEventListener('change', handleChange);

    return () => {
      query?.removeEventListener('change', handleChange);
    };
  }, [size]);

  return isSize;
}
